import { useMangaProgress } from '@/hooks/useMangaProgress'
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline'
import { UserGuard } from '@reader-tracker/shared/core/components/UserGuard'
import type { Chapter, Source } from '@reader-tracker/shared/lib/database.types'
import { supabase } from '@reader-tracker/shared/lib/supabase'
import { useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

interface ChapterWithSource extends Chapter {
  sources: Source
}

function ReaderContent() {
  const { chapterId } = useParams<{ chapterId: string }>()
  const navigate = useNavigate()
  const { saveProgressImmediate } = useMangaProgress()

  const [chapter, setChapter] = useState<ChapterWithSource | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isRedirecting, setIsRedirecting] = useState(true)

  useEffect(() => {
    if (!chapterId) return
    const resolvedChapterId = chapterId
    let cancelled = false

    async function trackAndRedirect() {
      setError(null)
      setIsRedirecting(true)

      const { data: ch, error: chapterErr } = await supabase
        .from('chapters')
        .select('*, sources(*)')
        .eq('id', resolvedChapterId)
        .single()

      if (cancelled) return

      if (chapterErr || !ch) {
        setError(chapterErr?.message ?? 'Chapter not found.')
        setIsRedirecting(false)
        return
      }

      const loadedChapter = ch as ChapterWithSource
      setChapter(loadedChapter)

      const progressResult = await saveProgressImmediate(
        loadedChapter.title_id,
        loadedChapter.id,
        0,
      )

      if (cancelled) return

      if (progressResult?.error) {
        setError(progressResult.error.message)
        setIsRedirecting(false)
        return
      }

      window.location.assign(loadedChapter.url)
    }

    trackAndRedirect()

    return () => {
      cancelled = true
    }
  }, [chapterId, saveProgressImmediate])

  return (
    <div className="flex min-h-dvh items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() =>
              chapter ? navigate(`/title/${chapter.title_id}`) : navigate(-1)
            }
            className="inline-flex items-center gap-2 rounded-lg px-2 py-1.5 text-gray-500 text-sm hover:bg-gray-100 hover:text-gray-800"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            Back
          </button>
          {chapter && (
            <span className="truncate text-gray-400 text-xs">
              {chapter.sources.name}
            </span>
          )}
        </div>

        {error ? (
          <div className="space-y-4">
            <div>
              <h1 className="font-semibold text-gray-900">
                Could not open chapter
              </h1>
              <p className="mt-1 text-red-600 text-sm">{error}</p>
            </div>
            {chapter && (
              <a
                href={chapter.url}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-sm text-white hover:bg-blue-700"
                rel="noopener noreferrer"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                Open on {chapter.sources.name}
              </a>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <h1 className="font-semibold text-gray-900">
                {isRedirecting ? 'Opening source…' : 'Ready to open'}
              </h1>
              <p className="mt-1 text-gray-500 text-sm">
                {chapter
                  ? `Chapter ${chapter.chapter_no}${
                      chapter.title ? ` — ${chapter.title}` : ''
                    }`
                  : 'Loading chapter details.'}
              </p>
            </div>

            {chapter && (
              <a
                href={chapter.url}
                className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 font-medium text-sm text-white hover:bg-blue-700"
                rel="noopener noreferrer"
              >
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                Open on {chapter.sources.name}
              </a>
            )}

            <Link
              to="/library"
              className="block text-center text-gray-500 text-sm hover:text-gray-800"
            >
              Return to library
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

export default function ReaderPage() {
  return (
    <UserGuard>
      <ReaderContent />
    </UserGuard>
  )
}
